import supabase from './supabaseClient';

async function readInvokeErrorBody(error) {
  if (!error) return '';
  try {
    if (error.context && typeof error.context.json === 'function') {
      const body = await error.context.json();
      if (body?.error) return String(body.error);
      if (body?.message) return String(body.message);
      if (typeof body === 'string') return body;
    }
  } catch {
    /* ignore parse failures */
  }
  try {
    if (error.context && typeof error.context.text === 'function') {
      const text = await error.context.text();
      if (text) return String(text).slice(0, 400);
    }
  } catch {
    /* ignore */
  }
  return '';
}

async function invokeErrorMessage(error, data) {
  if (data?.error) return String(data.error);
  const fromBody = await readInvokeErrorBody(error);
  if (fromBody) return fromBody;
  if (error?.message) {
    if (/non-2xx/i.test(error.message)) {
      return 'Billing service error (non-2xx). Redeploy stripe-billing-admin with JWT verification off, and run billing migrations if columns are missing.';
    }
    return error.message;
  }
  return 'Billing request failed';
}

async function invokeFunction(name, body) {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error || data?.error) {
    throw new Error(await invokeErrorMessage(error, data));
  }
  return data;
}

export async function createBillingCheckoutSession(organizationId) {
  return invokeFunction('stripe-create-checkout', {
    organizationId: organizationId || null,
  });
}

export async function fetchBillingOverview() {
  return invokeFunction('stripe-billing-admin', { action: 'overview' });
}

export async function setOrganizationPricing({ organizationId, unitAmountPounds, homeCountOverride }) {
  return invokeFunction('stripe-billing-admin', {
    action: 'set-pricing',
    organizationId,
    unitAmountPounds,
    homeCountOverride,
  });
}

export async function syncOrganizationBillingQuantity(organizationId) {
  try {
    return await invokeFunction('stripe-billing-admin', {
      action: 'sync-quantity',
      organizationId,
    });
  } catch (err) {
    console.warn('[billing] quantity sync failed:', err?.message || err);
    return null;
  }
}

export async function extendOrganizationGrace(organizationId, days = 7) {
  return invokeFunction('stripe-billing-admin', {
    action: 'extend-grace',
    organizationId,
    days,
  });
}

async function invokeBillingAdmin(body) {
  return invokeFunction('stripe-billing-admin', body);
}

export async function unlockOrganizationBilling(organizationId) {
  return invokeBillingAdmin({ action: 'unlock', organizationId });
}

export async function lockOrganizationBilling(organizationId) {
  return invokeBillingAdmin({ action: 'lock', organizationId });
}

export async function setOrganizationAccessMode(organizationId, mode) {
  return invokeBillingAdmin({ action: 'set-access', organizationId, mode });
}

export async function setOrganizationTrialEnd(organizationId, { trialEndsAt, addMonths } = {}) {
  return invokeBillingAdmin({ action: 'set-trial', organizationId, trialEndsAt, addMonths });
}

export async function setOrganizationSubscriptionStatus(organizationId, subscriptionStatus) {
  return invokeBillingAdmin({ action: 'set-subscription-status', organizationId, subscriptionStatus });
}

export async function setOrganizationActive(organizationId, isActive) {
  return invokeBillingAdmin({ action: 'set-org-active', organizationId, isActive });
}

export async function suspendOrganizationStaffLogins(organizationId) {
  return invokeBillingAdmin({ action: 'suspend-staff', organizationId });
}

export async function restoreOrganizationStaffLogins(organizationId) {
  return invokeBillingAdmin({ action: 'restore-staff', organizationId });
}

export async function fetchMyOrganizationBilling() {
  return invokeBillingAdmin({ action: 'my-billing' });
}

export async function createOrganizationBillingPortal() {
  return invokeBillingAdmin({ action: 'create-portal' });
}

export function stripeDashboardCustomerUrl(customerId) {
  if (!customerId) return null;
  return `https://dashboard.stripe.com/customers/${customerId}`;
}
